import type { AssessorLoadingRowData } from '@app-types';

export const NO_TIER_KEY = 'NO_TIER';
const NO_TIER_LABEL = 'No tier';
// The legend always lists these, so a Tier missing from one month does not vanish from the key.
const STANDARD_TIER_KEYS = ['TIER_1', 'TIER_2', 'TIER_3', 'TIER_4', NO_TIER_KEY];
const NO_TIER_COLOR = '#9ca3af';
const TIER_COLORS = ['#1d4ed8', '#3b82f6', '#06b6d4', '#10b981', '#8b5cf6', '#f59e0b'];

export interface AssessorLoadingTier {
  key: string;
  label: string;
  color: string;
}

export interface AssessorLoadingAssessor {
  id: number;
  name: string;
  /** Meetings of this Assessor across the whole month. */
  monthTotal: number;
}

/** One stacked segment: the Meetings of one Assessor under one Tier, read from `key` on each chart row. */
export interface AssessorLoadingSeries {
  key: string;
  assessorId: number;
  tierKey: string;
  tierLabel: string;
  color: string;
}

/** One calendar day: `day` / `label` plus a count per series key. */
export type AssessorLoadingChartRow = Record<string, string | number>;

export interface AssessorLoadingChartModel {
  rows: AssessorLoadingChartRow[];
  series: AssessorLoadingSeries[];
  /** Every Assessor with Meetings in the month, whatever the selection; the options of the Assessor filter. */
  allAssessors: AssessorLoadingAssessor[];
  /** The Assessors drawn: the selected ones, or all when nothing is selected. */
  assessors: AssessorLoadingAssessor[];
  /** Legend entries: Tier 1-4 and No tier always, plus any other Tier present in the month. */
  tiers: AssessorLoadingTier[];
  /** No Meetings in the month at all. */
  isEmpty: boolean;
  /** Meetings exist in the month, but none for the selected Assessors. */
  isSelectionEmpty: boolean;
  assessorDayTotal: (assessorId: number, day: string) => number;
}

export function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** Tier order follows the code (TIER_1 highest), never the editable name; "No tier" is last. */
function compareTierKeys(a: string, b: string): number {
  if (a === b) return 0;
  if (a === NO_TIER_KEY) return 1;
  if (b === NO_TIER_KEY) return -1;
  return a.localeCompare(b, undefined, { numeric: true });
}

// "TIER_3" -> "Tier 3", for a standard Tier the month has no row (and so no name) for.
function fallbackTierLabel(key: string): string {
  if (key === NO_TIER_KEY) return NO_TIER_LABEL;
  const tierNumber = /(\d+)$/.exec(key)?.[1];
  return tierNumber ? `Tier ${tierNumber}` : key;
}

/** Key of one stacked segment on a chart row; the pivot and the series list must agree on it. */
function seriesKey(assessorId: number, tierKey: string): string {
  return `${assessorId}__${tierKey}`;
}

// Colour follows the number in the code so a Tier keeps its colour when another Tier is absent from the month.
function tierColor(key: string, fallbackIndex: number): string {
  if (key === NO_TIER_KEY) return NO_TIER_COLOR;
  const tierNumber = Number(/(\d+)$/.exec(key)?.[1]);
  const index = Number.isInteger(tierNumber) && tierNumber > 0 ? tierNumber - 1 : fallbackIndex;
  return TIER_COLORS[index % TIER_COLORS.length];
}

function daysOfMonth(month: string): string[] {
  const [year, monthNumber] = month.split('-').map(Number);
  const dayCount = new Date(year, monthNumber, 0).getDate();
  return Array.from({ length: dayCount }, (_, index) => `${month}-${String(index + 1).padStart(2, '0')}`);
}

/**
 * Pivots the flat API rows into one chart row per day of the month. Each Assessor is its own stack
 * (series share a stackId per Assessor) and each stack is segmented by Tier. An empty
 * `selectedAssessorIds` draws every Assessor.
 */
export function buildAssessorLoadingChart(
  month: string,
  data: AssessorLoadingRowData[],
  selectedAssessorIds: number[] = []
): AssessorLoadingChartModel {
  const rowsInMonth = data.filter((item) => item.day.startsWith(`${month}-`));

  const assessorNames = new Map<number, string>();
  const assessorTotals = new Map<number, number>();
  const tierLabels = new Map<string, string>();
  for (const item of rowsInMonth) {
    assessorNames.set(item.assessor_id, item.assessor_name);
    assessorTotals.set(item.assessor_id, (assessorTotals.get(item.assessor_id) ?? 0) + item.count);
    const tierKey = item.tier_code ?? NO_TIER_KEY;
    tierLabels.set(tierKey, item.tier_name ?? fallbackTierLabel(tierKey));
  }

  const allAssessors = Array.from(assessorNames, ([id, name]) => ({ id, name, monthTotal: assessorTotals.get(id) ?? 0 }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);

  const selected = new Set(selectedAssessorIds);
  const assessors = selected.size === 0 ? allAssessors : allAssessors.filter((assessor) => selected.has(assessor.id));
  const drawnRows = rowsInMonth.filter((item) => assessors.some((assessor) => assessor.id === item.assessor_id));

  const tierKeys = new Set(STANDARD_TIER_KEYS.concat(Array.from(tierLabels.keys())));
  const tiers = Array.from(tierKeys).sort(compareTierKeys).map((key, index) => ({
    key,
    label: tierLabels.get(key) ?? fallbackTierLabel(key),
    color: tierColor(key, index)
  }));

  // Series only for Tiers present in the month, so absent Tiers add no empty bars.
  const presentTiers = tiers.filter((tier) => tierLabels.has(tier.key));
  const series = assessors.flatMap((assessor) =>
    presentTiers.map((tier) => ({
      key: seriesKey(assessor.id, tier.key),
      assessorId: assessor.id,
      tierKey: tier.key,
      tierLabel: tier.label,
      color: tier.color
    }))
  );

  const rows = daysOfMonth(month).map((day): AssessorLoadingChartRow => ({ day, label: String(Number(day.slice(-2))) }));
  const rowByDay = new Map(rows.map((row) => [row.day as string, row]));
  const dayTotals = new Map<string, number>();

  for (const item of drawnRows) {
    const row = rowByDay.get(item.day);
    if (!row) continue;
    const key = seriesKey(item.assessor_id, item.tier_code ?? NO_TIER_KEY);
    row[key] = ((row[key] as number | undefined) ?? 0) + item.count;
    const totalKey = `${item.assessor_id}|${item.day}`;
    dayTotals.set(totalKey, (dayTotals.get(totalKey) ?? 0) + item.count);
  }

  return {
    rows,
    series,
    allAssessors,
    assessors,
    tiers,
    isEmpty: rowsInMonth.length === 0,
    isSelectionEmpty: rowsInMonth.length > 0 && drawnRows.length === 0,
    assessorDayTotal: (assessorId, day) => dayTotals.get(`${assessorId}|${day}`) ?? 0
  };
}
